import { Box } from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { customEvent } from "goi_common";
import { BaseButton } from "goi_common/src/design-system";
import useIntersectionObserver from "goi_common/src/hooks/useIntersectionObserver";

/**
 * 하단 고정 CTA 가 차지한 높이를 Layout 에 알리는 CSS 변수.
 *
 * Layout 의 체크리스트 FAB(`StepGuideFooter`)이 `calc(기본값 + var(이 변수, 0px))` 로 읽는다.
 * 변수를 세팅하지 않는 화면(A안, 다른 페이지 전부)에서는 `0px` 로 떨어져 현행 그대로다.
 *
 * 왜 Layout 의 라우트별 오프셋(`getChecklistFabBottomOffset`)을 안 쓰는가
 * - 그 값은 **라우트 단위**라 A안에도 같이 적용된다. 대조군 화면이 바뀌면 실험이 깨진다.
 * - 이 버튼은 스크롤 위치에 따라 떴다 사라진다. 라우트 단위 상수로는 표현할 수 없다.
 */
const FAB_OFFSET_VAR = "--goi-fixed-cta-offset";

/** 고정 CTA 와 그 위로 밀려나는 FAB 사이 간격(px) */
const FAB_GAP = 12;

interface RegionCostFrameFixedCtaProps {
  /** 버튼 문구. 히어로 제목과 같은 말을 쓴다 (`buildRegionCostFrameCopy.fixedCtaText`) */
  label: string;
  /** 가시성을 관찰할 대상. 히어로 카드를 그대로 넘긴다 */
  children: ReactNode;
}

/**
 * 히어로가 화면 밖으로 나가 있는 동안만 뜨는 하단 고정 CTA. 누르면 히어로로 되돌린다.
 *
 * 왜 필요한가
 * - 시도/시군구 지면에는 고정 CTA 가 하나도 없다. `CallFixedButton` 은 service 지면 전용이라
 *   스크롤로 폼을 지나치면 다시 돌아갈 길이 사라진다.
 * - 2026-08 실측(봇 제외): 90% 스크롤 세션 2,888건 중 2,041건(70.7%)이 제출 없이 끝났고,
 *   312건은 스크롤한 뒤 되돌아와 제출했다. 돌아오는 행동은 이미 있는데 길이 없는 상태다.
 *
 * 왜 별도 컴포넌트인가 (중요)
 * - `RegionCostFrameHero` 안에 두면 ref 가 **배정 판정 뒤**에 붙는다. `useCustomVariation` 은
 *   첫 렌더에 "Z"(배정 전)를 주고 그때는 대조군을 렌더하므로 ref 가 DOM 에 없다. 옵저버
 *   effect 는 그 시점에 한 번 돌고 `target.current` 가 null 이라 그대로 빠지는데, ref 객체는
 *   같은 참조라 배정이 끝나도 effect 가 다시 돌지 않는다. 버튼이 영원히 안 뜬다(실측 확인).
 * - 컴포넌트를 분리해 B·C 일 때만 마운트하면 ref 부착과 effect 실행 순서가 보장된다.
 */
export default function RegionCostFrameFixedCta({ label, children }: RegionCostFrameFixedCtaProps) {
  const heroRef = useRef<HTMLDivElement>(null);

  /*
   * 초기값 true: SSG 첫 화면에서 히어로는 최상단에 보인다. false 로 시작하면 하이드레이션
   * 직후 한 프레임 동안 버튼이 번쩍인다.
   */
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const handleIntersect = useCallback(() => setIsHeroVisible(true), []);
  const handleNotIntersect = useCallback(() => setIsHeroVisible(false), []);

  useIntersectionObserver({
    root: null,
    target: heroRef,
    onIntersect: handleIntersect,
    onNotIntersect: handleNotIntersect,
    // 1px 이라도 걸쳐 있으면 아직 "보인다". 카드가 완전히 지나간 뒤에만 버튼이 뜬다
    threshold: 0,
    rootMargin: "0px",
  });

  const handleClick = useCallback(() => {
    customEvent({
      newGtm: { path: "parlor_region", trigger_and_target: "fixed_cta-scrl_to_form" },
    });
    heroRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  /*
   * 버튼이 떠 있는 동안만 체크리스트 FAB 을 그 높이만큼 위로 민다.
   *
   * 실측값을 쓰는 이유: 지역명이 길어 문구가 두 줄이 되면 버튼 높이가 달라진다. 상수로 박으면
   * 그 지면에서만 조용히 다시 겹친다.
   *
   * 2026-08 기준 시도/시군구 면 FAB 클릭은 월 141건이고 그중 53건이 스크롤 뒤, 즉 이 버튼이
   * 떠 있는 바로 그 구간이다. 가리면 그만큼이 사라져 실험 결과가 오염된다.
   */
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;

    if (isHeroVisible) {
      root.style.removeProperty(FAB_OFFSET_VAR);
      return;
    }

    const height = ctaRef.current?.getBoundingClientRect().height ?? 0;
    root.style.setProperty(FAB_OFFSET_VAR, `${Math.round(height) + FAB_GAP}px`);

    return () => {
      root.style.removeProperty(FAB_OFFSET_VAR);
    };
  }, [isHeroVisible]);

  return (
    <>
      <Box ref={heroRef} width="100%">
        {children}
      </Box>

      {/*
       * 바닥에 붙이고, 체크리스트 FAB 을 이 버튼 높이만큼 위로 민다(위 effect).
       *
       * 처음에는 반대로 이 버튼을 FAB 위(98px)에 띄웠다. 그러면 지면마다 다른 FAB 오프셋
       * (`getChecklistFabBottomOffset`)을 이쪽이 추측해야 하고, 값이 하나라도 어긋나면 조용히
       * 다시 겹친다. 미는 쪽이 실측값을 쓰므로 어긋날 여지가 없다.
       *
       * 숨길 때 `translateY(100%)` 를 쓰지 않는다. safe-area 만큼 떠 있어 100% 를 내려도 화면에
       * 남을 수 있다. opacity + pointer-events 로 확실히 없앤다.
       *
       * 배경 바 없이 버튼만 띄운다. 흰 바를 깔면 위로 밀려난 FAB 과 두 덩어리로 보인다.
       * zIndex 는 `CallFixedButton`(1040) 아래. 이 지면엔 없지만 나중에 들어와도 통화 버튼을
       * 가리지 않는 쪽이 맞다.
       */}
      <Box
        ref={ctaRef}
        position="fixed"
        bottom="calc(20px + env(safe-area-inset-bottom, 0px))"
        left="50%"
        transform={`translateX(-50%) translateY(${isHeroVisible ? "8px" : "0"})`}
        opacity={isHeroVisible ? 0 : 1}
        pointerEvents={isHeroVisible ? "none" : "auto"}
        transition="opacity 0.25s ease-out, transform 0.25s ease-out"
        width="calc(100% - 40px)"
        maxWidth="440px"
        boxShadow="0 4px 16px rgba(0, 0, 0, 0.18)"
        borderRadius="10px"
        zIndex={1030}
      >
        <BaseButton type="primary" size="large" onClick={handleClick}>
          <Box className="body_1_normal_m">{label}</Box>
        </BaseButton>
      </Box>
    </>
  );
}
